import { z } from "zod";

// =============================================================================
// Account-self management — endpoints under /api/account
//
// Distinct from /api/admin: these always operate on the current user.
// Anyone authenticated can hit them; admins use them to manage their
// own profile too.
// =============================================================================

// ----- Profile -------------------------------------------------------------

/**
 * UI theme preference, stored per-user.
 *
 * - `"unset"`  — user has never picked → the frontend opens the
 *               first-time picker on next session bootstrap. Default
 *               for every existing row at the time of the launch
 *               so the 100+ active users get prompted once.
 * - `"light"`  — explicit light theme.
 * - `"dark"`   — explicit dark theme (the legacy / original look).
 * - `"system"` — follow `prefers-color-scheme` and re-resolve live
 *               when the OS flips.
 *
 * Modeled as an enum here (not just `string`) so the client-side
 * theme provider can be exhaustively narrowed in a switch; the DB
 * column is `text` to leave room for future values like
 * `"high-contrast"` without a migration.
 */
export const themePreferenceSchema = z.enum([
  "unset",
  "light",
  "dark",
  "system",
]);
export type ThemePreference = z.infer<typeof themePreferenceSchema>;

export const accountProfileSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  /** Optional callback phone the user wants the trip form to auto-fill
   *  on every booking. Empty string when the user hasn't supplied one. */
  phone: z.string(),
  role: z.enum(["super_admin", "admin", "user"]),
  department: z.string(),
  /** UI theme. `"unset"` triggers the first-time picker; anything
   *  else is the value the ThemeProvider applies on session bootstrap. */
  themePreference: themePreferenceSchema,
});
export type AccountProfile = z.infer<typeof accountProfileSchema>;

export const accountProfileUpdateRequestSchema = z
  .object({
    firstName: z
      .string()
      .min(1)
      .max(50)
      .transform((v) => v.trim())
      .optional(),
    lastName: z
      .string()
      .min(1)
      .max(50)
      .transform((v) => v.trim())
      .optional(),
    /** Phone is optional in two senses: the user can save no phone at
     *  all (empty string), and the field itself is optional in the
     *  request body so an existing UI that doesn't know about phone
     *  can still PATCH name fields without clearing it. We accept any
     *  shape of US phone here and let the API normalize to digits-only. */
    phone: z
      .string()
      .max(40)
      .transform((v) => v.trim())
      .optional(),
  })
  .refine(
    (v) => Object.keys(v).length > 0,
    "Provide at least one field to update.",
  );
export type AccountProfileUpdateRequest = z.infer<
  typeof accountProfileUpdateRequestSchema
>;

export const accountProfileResponseSchema = z.object({
  profile: accountProfileSchema,
});
export type AccountProfileResponse = z.infer<typeof accountProfileResponseSchema>;

// ----- Theme preference ---------------------------------------------------
//
// Dedicated endpoint instead of folding theme into `/profile` because
// theme updates fire every click of the toggle (and a first-time pick
// from the dialog), while profile updates are intentional and rare.
// Keeping them split lets us audit-log the two events differently and
// rate-limit theme without raising the bar on actual profile edits.

export const accountThemeUpdateRequestSchema = z.object({
  /** Target theme. `"unset"` is rejected — the picker only writes
   *  one of the three concrete values; "unset" is the *initial* state
   *  the migration leaves rows in, not something a client should be
   *  able to set after the fact. */
  theme: themePreferenceSchema.refine(
    (v) => v !== "unset",
    "Cannot set the theme back to 'unset' from the client; that's the migration default.",
  ),
});
export type AccountThemeUpdateRequest = z.infer<
  typeof accountThemeUpdateRequestSchema
>;

export const accountThemeUpdateResponseSchema = z.object({
  themePreference: themePreferenceSchema,
});
export type AccountThemeUpdateResponse = z.infer<
  typeof accountThemeUpdateResponseSchema
>;

// =============================================================================
// /api/account/feedback — in-app feedback / bug-report / feature-request
//
// The customer's preferred channel for product input from the 100+
// active dispatchers on the system. Routes to `service@wisdomweave.com`
// via SendGrid. Auth-required (every submission has a logged-in
// user attached) and rate-limited per-user (5/min) so a bad actor
// can't blast the inbox. The server adds context (URL, version,
// browser) from the request headers / session — the client only
// has to supply the category + the user's message.
// =============================================================================

export const feedbackCategorySchema = z.enum([
  "bug",
  "idea",
  "question",
  "other",
]);
export type FeedbackCategory = z.infer<typeof feedbackCategorySchema>;

export const accountFeedbackSubmitRequestSchema = z.object({
  category: feedbackCategorySchema,
  /** 10 - 2000 chars. Lower bound nudges the user to write something
   *  parseable; upper bound caps SendGrid payload + audit log row. */
  message: z.string().trim().min(10).max(2000),
  /** Window URL at the moment they clicked Send — included so we can
   *  reproduce what they were looking at. The client volunteers this
   *  because the server can't otherwise see the client-side route. */
  pageUrl: z.string().max(500).optional(),
  /** Document title at the time of click. Useful when the URL is
   *  generic but the page identity isn't (e.g. `/admin/bookings` could
   *  be filtered to a specific patient — the title carries that). */
  pageTitle: z.string().max(200).optional(),
  /** "1440x900" — screen geometry, useful for visual-bug repros. */
  viewportSize: z.string().max(20).optional(),
});
export type AccountFeedbackSubmitRequest = z.infer<
  typeof accountFeedbackSubmitRequestSchema
>;

export const accountFeedbackSubmitResponseSchema = z.object({
  sent: z.literal(true),
});
export type AccountFeedbackSubmitResponse = z.infer<
  typeof accountFeedbackSubmitResponseSchema
>;

// ----- Password change -----------------------------------------------------

export const accountPasswordChangeRequestSchema = z.object({
  /** Plain "I forgot my old password" requires the password-reset flow,
   *  not this endpoint — current password proves the session belongs
   *  to the same person who knows the password. */
  currentPassword: z.string().min(1).max(500),
  newPassword: z.string().min(8).max(500),
});
export type AccountPasswordChangeRequest = z.infer<
  typeof accountPasswordChangeRequestSchema
>;

export const accountPasswordChangeResponseSchema = z.object({
  passwordChanged: z.literal(true),
});
export type AccountPasswordChangeResponse = z.infer<
  typeof accountPasswordChangeResponseSchema
>;

// ----- Sessions ------------------------------------------------------------

export const accountSessionSchema = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  lastActivityAt: z.string(),
  expiresAt: z.string(),
  ipAddress: z.string(),
  userAgent: z.string(),
  isCurrent: z.boolean(),
});
export type AccountSession = z.infer<typeof accountSessionSchema>;

export const accountSessionsResponseSchema = z.object({
  sessions: z.array(accountSessionSchema),
});
export type AccountSessionsResponse = z.infer<typeof accountSessionsResponseSchema>;

export const accountEmptyOkResponseSchema = z.object({ ok: z.literal(true) });
export type AccountEmptyOkResponse = z.infer<typeof accountEmptyOkResponseSchema>;

// =============================================================================
// Password reset (public, no auth required) — under /api/auth
// =============================================================================

export const passwordResetRequestSchema = z.object({
  email: z
    .string()
    .min(3)
    .max(200)
    .transform((v) => v.trim().toLowerCase()),
});
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

export const passwordResetRequestResponseSchema = z.object({
  sent: z.literal(true),
});
export type PasswordResetRequestResponse = z.infer<
  typeof passwordResetRequestResponseSchema
>;

export const passwordResetConsumeRequestSchema = z.object({
  token: z.string().min(10).max(200),
  newPassword: z.string().min(8).max(500),
});
export type PasswordResetConsumeRequest = z.infer<
  typeof passwordResetConsumeRequestSchema
>;

export const passwordResetConsumeResponseSchema = z.object({
  passwordReset: z.literal(true),
});
export type PasswordResetConsumeResponse = z.infer<
  typeof passwordResetConsumeResponseSchema
>;
